import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Stack,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DataTable from "../../components/DataTable";
import { useSnackbar } from "../../context/SnackbarContext";
import { stockService } from "../../services/stock/stockService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatDate } from "../../utils/date";

interface ArrivedFormData {
  observation: string;
}

interface StockDetail {
  stockHeaderId: string;
  code: string;
  observation: string;
  estimatedArribalDate: string;
  arribalDate?: string;
  percentageSafety: number;
  company: string;
  status: string;
  createDate: string;
}

interface ArticleItem {
  stockItemId: string;
  productId: string;
  productCode: string;
  productDescription: string;
  quantity: number;
  rest: number;
}

export const StockViewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stockData, setStockData] = useState<StockDetail | null>(null);
  const [articlePage, setArticlePage] = useState<number>(0);
  const [articleRowsPerPage] = useState<number>(10);
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const [articlesTotal, setArticlesTotal] = useState<number>(0);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(false);
  const [productSearch, setProductSearch] = useState<string>("");
  const [arrivedModalOpen, setArrivedModalOpen] = useState<boolean>(false);
  const [isArriving, setIsArriving] = useState<boolean>(false);

  // Verificar si el query param arribal es true
  const showArribalButton = searchParams.get("arribal") === "true";

  const {
    control: arrivedControl,
    handleSubmit: handleArrivedSubmit,
    formState: { errors: arrivedErrors },
    reset: resetArrivedForm,
  } = useForm<ArrivedFormData>({
    defaultValues: {
      observation: "",
    },
  });

  useEffect(() => {
    const fetchStockData = async () => {
      if (!id) {
        showSnackbar("ID de stock no proporcionado", "error", 3000);
        navigate("/stock");
        return;
      }

      try {
        setIsLoading(true);
        const response = await stockService.getStockById(id);
        setStockData(response as any);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar el stock",
          "error",
          4000
        );
        // navigate("/stock");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStockData();
  }, [id, navigate, showSnackbar]);

  // Cargar items del stock con paginación y búsqueda
  useEffect(() => {
    const fetchItems = async () => {
      if (!id) return;

      try {
        setLoadingArticles(true);
        const response = await stockService.getItems(
          id,
          articlePage,
          articleRowsPerPage,
          productSearch
        );
        setArticles(response?.stockItemResponses || response?.items || []);
        setArticlesTotal(response?.total || 0);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar los items",
          "error",
          4000
        );
        setArticles([]);
        setArticlesTotal(0);
      } finally {
        setLoadingArticles(false);
      }
    };

    // Debounce para la búsqueda
    const delayDebounceFn = setTimeout(() => {
      fetchItems();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [id, articlePage, articleRowsPerPage, productSearch, showSnackbar]);

  const handleBack = () => {
    navigate("/stock");
  };

  const handleArticlePageChange = (page: number) => {
    const newPage = page - 1; // DataTable usa páginas basadas en 1, API usa basadas en 0
    setArticlePage(newPage);
  };

  const handleClearSearch = () => {
    setProductSearch("");
    setArticlePage(0);
  };

  const handleOpenArrivedModal = () => {
    resetArrivedForm({ observation: "" });
    setArrivedModalOpen(true);
  };

  const handleCloseArrivedModal = () => {
    setArrivedModalOpen(false);
    resetArrivedForm();
  };

  const onArrivedStock = async (data: ArrivedFormData) => {
    if (!id) return;

    try {
      setIsArriving(true);
      await stockService.arrivedStock(id, data.observation);
      showSnackbar("Stock marcado como arribado exitosamente", "success", 3000);
      handleCloseArrivedModal();
      navigate("/stock");
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al marcar el stock como arribado",
        "error",
        4000
      );
    } finally {
      setIsArriving(false);
    }
  };

  const articleColumns = [
    {
      header: "Código Producto",
      key: "code",
    },
    {
      header: "Nombre",
      key: "description",
    },
    {
      header: "Cantidad",
      key: "quantity",
    },
    {
      header: "Resto",
      key: "rest",
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!stockData) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/stock")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Stocks
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Ver Stock
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Detalle del Stock
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Información completa del stock
            </Typography>
          </Box>

          {/* Form Fields - Read Only */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Código"
                    value={stockData.code || ""}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Estado"
                    value={stockData.status || ""}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Fecha Estimada de Llegada"
                    value={formatDate(stockData.estimatedArribalDate) || ""}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Fecha de Llegada"
                    value={formatDate(stockData.arribalDate || "") || ""}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      readOnly: true,
                    }}
                   
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Sociedad"
                    value={stockData.company || ""}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Porcentaje de Fallo"
                    value={stockData.percentageSafety || 0}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <TextField
                  label="Observación"
                  value={stockData.observation || ""}
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Articles Section */}
          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" component="h2">
                Listado de Productos
              </Typography>
            </Box>

            {/* Filtro de búsqueda */}
            <Box sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: "center" }}
              >
                <TextField
                  label="Buscar producto"
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setArticlePage(0);
                  }}
                  placeholder="Buscar por código o nombre..."
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  Limpiar
                </Button>
              </Stack>
            </Box>

            <DataTable
              columns={articleColumns}
              data={articles}
              page={articlePage + 1}
              isLoading={loadingArticles}
              onChange={handleArticlePageChange}
              total={articlesTotal}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 3,
              justifyContent: 'space-between'
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Volver
            </Button>
            {showArribalButton && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleOpenArrivedModal}
                sx={{ minWidth: 120 }}
                disabled={isLoading}
              >
                Arribar stock
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Modal de Arribo */}
      <Dialog
        open={arrivedModalOpen}
        onClose={handleCloseArrivedModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Arribar Stock
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Controller
              name="observation"
              control={arrivedControl}
              rules={{
                required: "La observación es requerida",
                
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observación"
                  multiline
                  rows={4}
                  fullWidth
                  error={!!arrivedErrors.observation}
                  helperText={arrivedErrors.observation?.message}
                  placeholder="Ingrese observaciones sobre el arribo del stock..."
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseArrivedModal} 
            variant="outlined"
            disabled={isArriving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleArrivedSubmit(onArrivedStock)}
            variant="contained"
            color="warning"
            disabled={isArriving}
          >
            {isArriving ? "Arribando..." : "Confirmar Arribo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockViewPage;
